/* eslint-disable @typescript-eslint/member-ordering */
import {DateRange} from "./date-range";
import {action, computed, makeObservable, observable} from "mobx";
import {FieldOverrideConfiguration} from "../config/field-override-configuration";

export class TimeSeries {

    /**
     * return the selected period a combination of from, to, period
     * The following combinations of time periods can be requested:
     * from + to:	    will return the requested range
     * from + period:	will return the given period starting at the from date
     * to + period:	    will return the given period backdating from the to date
     * period:	        will return the given period backdating from the current system time
     * period=complete:	will return the complete coverage of all requested timeseries
     * from:	        will return all data starting at the given from date until the current system time
     * to:	            will return all data of the timeseries until the given to date
     *
     * If no period is selected, return a period of 1 week up to the last available value (coverage)
     */
    @computed
    get selectedDateRange(): DateRange | null {
        if (!this.coverage || (!this.coverage.from && !this.coverage.to)) {
            // no data available
            return null;
        }
        if (this.from && this.to) {
            return new DateRange({ from: this.from, to: this.to });
        }
        if (this.from && this.periodISO) {
            return new DateRange({ from: this.from, iso: this.periodISO });
        }
        if (this.to && this.periodISO) {
            return new DateRange({ to: this.to, iso: this.periodISO });
        }
        if (this.periodISO) {
            return new DateRange({ to: this.coverage.to, iso: this.periodISO });
        }
        if (this.from) {
            return new DateRange({ from: this.from, iso: this.DEFAULT_PERIOD });
        }
        if (this.to) {
            return new DateRange({ to: this.to, iso: this.DEFAULT_PERIOD });
        }
        // no selected period --> return DEFAULT_PERIOD
        if (this.coverage) {
            return new DateRange({ to: this.coverage.to, iso: this.DEFAULT_PERIOD });
        }
        return null;
    }

    /**
     * Compare two time series objects
     *
     * @param ts1
     * @param ts2
     */
    static compare(ts1: TimeSeries, ts2: TimeSeries) {
        const name1 = ts1.stationParameterName.toUpperCase();
        const name2 = ts2.stationParameterName.toUpperCase();
        if (name1 < name2) {
            return -1;
        }

        if (name1 > name2) {
            return 1;
        }

        return 0;
    }

    DEFAULT_PERIOD = "PT24H";

    stationId: number; // station_id
    @observable id: string;  // ts_id
    @observable name: string; // ts_name

    typeName: string; // ts_type_name

    /**
     * Topics are not directly stored on time series in WISKI.
     * The correlation between the time series and a topic is solved through groups.
     * Time series are grouped by topic in WISKI 'timeseries' groups. These groups are used to derive the topic.
     * A time series can belong to multiple topics.
     */
    topics: string[];

    @observable parameterTypeName: string;
    stationParameterName: string;

    unitName: string; // ts_unitname
    unitSymbol: string; // ts_unitsymbol
    // noinspection JSUnusedGlobalSymbols
    unitNameAbsolute: string; // ts_unitname_abs
    unitSymbolAbsolute: string; // ts_unitsymbol_abs

    @observable coverage: DateRange; // time period with available data values

    /**
     * selected period is specified by 'from/to' and/or 'period'
     *
     * The following combinations of time periods can be requested:
     * from + to:	    will return the requested range
     * from + period:	will return the given period starting at the from date
     * to + period:	    will return the given period backdating from the to date
     * period:	        will return the given period backdating from the current system time
     * period=complete:	will return the complete coverage of all requested timeseries
     * from:	        will return all data starting at the given from date until the current system time
     * to:	            will return all data of the timeseries until the given to date
     */
    @observable from: string;
    @observable to: string;

    /**
     * When aggregating time series, the data contains only the dates of the aggregation points.
     * To have access to dates of the originally requested data, the from and to dates of the request are stored in
     * these variables.
     */
    @observable dataRequestedFromDate: Date;
    @observable dataRequestedToDate: Date;

    /**
     * available values for period:
     * ISO format P#Y#M#DT#H#M#S where P just indicates an arbitrary period with the # being integers
     * Y=Year, M=Month, D=Day, T just indicates that smaller entities are included, H=Hour, M=Minute,
     * S=Second; W=Week is also supported but mutually exclusive with D=Day setting;
     *
     * Examples: P3D, P1Y, P1DT12H, PT6H, P1Y6M3DT4H20M30S;
     * all periods are applied by calendar; as an alternative period can be set to 'complete' to return all data
     */
    @observable periodISO: string;

    density: string; // ts_density will add an estimation of how many values this timeseries theoretically has per year
    exchange: string; // ts_exchange will add the timeseries exchange ID in case it exists
    spacing: string; // ts_spacing: value spacing of equidistant timeseries as ISO period

    /**
     * Position of relative value in data array
     * This is time series specific and depends on configuration in WISKI
     * Index is only set when a relative value is present
     */
    @observable dataRelativeValueIndex: number | null;

    /**
     * Position of absolute value in data array
     * This is time series specific and depends on configuration in WISKI
     */
    @observable dataAbsoluteValueIndex: number | null;

    constructor(
        props?: {
            stationId?: number;
            id?: string;
            name?: string;
            typeName?: string;

            topics?: string[];

            parameterTypeName?: string;
            stationParameterName?: string;

            unitName?: string;
            unitSymbol?: string;
            unitNameAbsolute?: string;
            unitSymbolAbsolute?: string;

            coverage?: DateRange;
            from?: string;
            to?: string;
            periodISO?: string;

            density?: string;
            exchange?: string;
            spacing?: string;
        }) {
        if (props) {
            Object.assign(this, props);
        }
        makeObservable(this); // make observable after setting properties to avoid 'strict' messages
    }

    /**
     * Set index of absolute value in data tuple
     */
    @action
    setDataAbsoluteValueIndex(index: number | null) {
        this.dataAbsoluteValueIndex = index;
    }

    /**
     * Set index of relative value in data tuple
     */
    @action
    setDataRelativeValueIndex(index: number | null) {
        this.dataRelativeValueIndex = index;
    }

    /**
     * Set current ISO period for this time series
     */
    @action
    setPeriodISO(period: string) {
        this.periodISO = period;
    }

    /**
     * Apply overrides
     * Can be used to change original values
     */
    applyOverrides(overrides: FieldOverrideConfiguration[]) {
        if (!overrides || !overrides.length) {
            return;
        }

        const applicableOverrides = overrides.filter((ov) =>
            ov.parameterTypes.indexOf(this.parameterTypeName) !== -1
        );

        // noinspection DuplicatedCode
        const that = this;
        applicableOverrides.forEach((over: FieldOverrideConfiguration) => {
            if (over.queryExpression.evaluate(that)) {
                let value = over.valueExpression;
                if (value.includes("${")) {
                    value = `${value.evaluate(that)}`;
                }
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                that[over.fieldName] = value;
            }
        });
    }

}
